import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAppContext, type DecryptedBlock } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import { encrypt } from "@/utls/CryptoHelpers";

function stringifyPayload(block: DecryptedBlock): string {
  return JSON.stringify({ id: block.id, type: block.type, data: block.data }, null, 2);
}

function normalizePayloadJson(args: {
  raw: unknown;
  expectedBlockId: string;
}): { ok: true; block: DecryptedBlock } | { ok: false; message: string } {
  const { raw, expectedBlockId } = args;
  if (raw === null || typeof raw !== "object") {
    return { ok: false, message: "JSON must be an object with id, optional type, and data." };
  }
  const obj = raw as Record<string, unknown>;
  const id = obj.id;
  if (typeof id !== "string" || id !== expectedBlockId) {
    return {
      ok: false,
      message: `JSON "id" must be the string "${expectedBlockId}" (matching this block).`,
    };
  }
  if (!Object.prototype.hasOwnProperty.call(obj, "data")) {
    return { ok: false, message: 'JSON must include a "data" property.' };
  }
  const type = obj.type;
  if (type !== undefined && typeof type !== "string") {
    return { ok: false, message: '"type", if present, must be a string.' };
  }
  return {
    ok: true,
    block: { id, type, data: obj.data },
  };
}

export function DebugBlockRoute() {
  const { blockId } = useParams<{ blockId: string }>();
  const { blocks, isLoading, updateDecryptedBlock } = useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();

  const block = useMemo(() => {
    if (!blockId) return undefined;
    return blocks.find((b) => b.id === blockId);
  }, [blocks, blockId]);

  const payload = useMemo(() => {
    if (!block) return null;
    return { id: block.id, type: block.type, data: block.data };
  }, [block]);

  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    if (!block) return;
    setDraftText(stringifyPayload(block));
  }, [block]);

  useEffect(() => {
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: "Block JSON (debug)" },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [setBreadcrumbSegments]);

  const isDirty = useMemo(() => {
    if (!block) return false;
    try {
      const parsedDraft = JSON.parse(draftText) as unknown;
      const parsedBlock = normalizePayloadJson({
        raw: parsedDraft,
        expectedBlockId: block.id,
      });
      if (!parsedBlock.ok) return true;
      return (
        JSON.stringify(parsedBlock.block) !==
        JSON.stringify({ id: block.id, type: block.type, data: block.data })
      );
    } catch {
      return true;
    }
  }, [block, draftText]);

  const [parseError, setParseError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const persistBlockEncrypted = useCallback(
    async ({ block }: { block: DecryptedBlock }): Promise<boolean> => {
      const secretKey =
        typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
      if (!secretKey) {
        console.warn("DebugBlockRoute: no accessKey in localStorage; cannot persist");
        return false;
      }
      const encryptedData = await encrypt(JSON.stringify(block.data), secretKey);
      const res = await fetch(`/api/blocks/${encodeURIComponent(block.id)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      });
      if (!res.ok) {
        console.error("DebugBlockRoute: persist failed", await res.text());
        return false;
      }
      return true;
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!block || !blockId) return;
    setSaveError(null);
    let parsedUnknown: unknown;
    try {
      parsedUnknown = JSON.parse(draftText) as unknown;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Invalid JSON";
      setParseError(msg);
      return;
    }

    const normalized = normalizePayloadJson({
      raw: parsedUnknown,
      expectedBlockId: blockId,
    });
    if (!normalized.ok) {
      setParseError(normalized.message);
      return;
    }
    setParseError(null);

    const secretKey =
      typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
    if (!secretKey) {
      setSaveError("Missing accessKey in localStorage.");
      return;
    }

    setIsSaving(true);
    try {
      const ok = await persistBlockEncrypted({ block: normalized.block });
      if (!ok) {
        setSaveError("Save failed.");
        return;
      }
      updateDecryptedBlock({ block: normalized.block });
    } finally {
      setIsSaving(false);
    }
  }, [block, blockId, draftText, persistBlockEncrypted, updateDecryptedBlock]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-base font-semibold leading-none tracking-tight">
          Raw block JSON
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              !isDirty
                ? "text-xs text-muted-foreground"
                : "text-xs text-amber-600 dark:text-amber-500"
            }
          >
            {!isDirty ? "Saved" : "Unsaved"}
          </span>
          <Button
            type="button"
            disabled={!block || !isDirty || isSaving}
            className="gap-2"
            onClick={() => void handleSave()}
          >
            {isSaving ? <Loader2 className="size-4 shrink-0 animate-spin" /> : null}
            Save block
          </Button>
        </div>
      </div>
      {parseError && (
        <p className="text-sm text-destructive" role="alert">
          {parseError}
        </p>
      )}
      {saveError && (
        <p className="text-sm text-destructive" role="alert">
          {saveError}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
        {!isLoading && !payload && (
          <p className="text-muted-foreground text-sm">Block not found.</p>
        )}
        {!isLoading && payload && (
          <Textarea
            className="h-[min(70vh,calc(100vh-14rem))] min-h-[240px] w-full resize-y font-mono text-xs leading-relaxed"
            spellCheck={false}
            value={draftText}
            onChange={(e) => {
              setParseError(null);
              setSaveError(null);
              setDraftText(e.target.value);
            }}
          />
        )}
      </div>
    </div>
  );
}
